import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    axios.get('/api/auth/me')
      .then(res => {
        setUser(res.data.user);
        Sentry.setUser({ id: res.data.user.id, email: res.data.user.email, role: res.data.user.role });
      })
      .catch(() => {
        setUser(null);
        Sentry.setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post('/api/auth/login', { email, password });
    setUser(data.user);
    Sentry.setUser({ id: data.user.id, email: data.user.email, role: data.user.role });
    return data;
  };

  const logout = async () => {
    await axios.post('/api/auth/logout');
    setUser(null);
    Sentry.setUser(null);
    localStorage.removeItem('selectedLocationId');
    queryClient.clear();
  };

  // Permission helpers derived from user object
  const permissions = useMemo(() => {
    if (!user) return { isSuperAdmin: false, role: null };
    return {
      isSuperAdmin: user.role === 'super_admin',
      role: user.role,
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, ...permissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
